'use client';

import { useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { getProductName } from '@cat-ecom/common/utils/stringUtils';
import { getFamilyName } from 'services/equipment/utils';
import { SelectedEquipmentProps } from '../../types';
import { Image } from 'components/image';
import { SelectedEquipmentHeading } from './selected-equipment-heading/selected-equipment-heading';
import { EquipmetActionButtonWrapper } from './equipment-action-button-wrapper';
import { MyEquipmentContextProvider } from 'components/my-equipment/my-equipment-search-pagination-context';
import styles from './selected-equipment.module.scss';
import { ServiceInsightIcon } from 'components/my-equipment/service-insight-icon/ServiceInsightIcon';
import { CatButton } from 'components/blocks-components';
import { Conditional, useBreakpoint } from 'components/pcc-components';
import { PAGE_BREAKPOINTS } from 'constants/common';
import { AddSerialButton } from 'components/my-equipment/add-serial-button';
import { LOCATION_HASH_KEY } from 'components/my-equipment/constants';
import cx from 'classnames';
import Link from 'next/link';
import { PARTS_DIAGRAM_PATH } from 'constants/links';

export const SelectedEquipment = ({
  userDetail,
  selectedEquipment,
  myEquipmentList,
  selectedEquipmentIndex,
  serviceInsights,
  startTransition,
  isPending,
  isAssetLandingFeatureFlag,
  seoURL
}: Readonly<SelectedEquipmentProps>) => {
  const t = useTranslations();
  const isMD = useBreakpoint(PAGE_BREAKPOINTS.MD);
  const isBLG = useBreakpoint(PAGE_BREAKPOINTS.BLOCKS_LG);

  const {
    productFamily,
    serialNumber,
    imageURL,
    assetName,
    name,
    model,
    seoUrl
  } = selectedEquipment;

  const modelVariant = !assetName ? 'title' : 'body-sm';
  const prominentVariant = isMD ? 'headline' : 'title-sm';
  const nonProminentVariant = isMD ? 'body' : 'footnote';
  const modelVariantSM = !assetName ? prominentVariant : nonProminentVariant;
  const familyName = getFamilyName(model, name);
  const equipmentName = `${model} ${getProductName(familyName)}`;
  const getEquipmentHeading = (label, variant, showTooltip = true) => {
    return (
      <Conditional test={label}>
        <SelectedEquipmentHeading
          variant={variant}
          showTooltip={showTooltip}
          className={cx({
            'mb-2': !showTooltip && isBLG
          })}
        >
          {label}
        </SelectedEquipmentHeading>
      </Conditional>
    );
  };

  const getEquipmentImage = useCallback(() => {
    return (
      <Image
        alt={productFamily}
        fill
        src={imageURL}
        loading="lazy"
        className={styles['equipment-image']}
        placeholder="empty"
      />
    );
  }, [productFamily, imageURL]);

  const redirectToShopParts = `${seoUrl}${LOCATION_HASH_KEY.SHOP_PARTS}`;
  const redirectToPartsDiagram = `${seoURL}${PARTS_DIAGRAM_PATH}`;

  return (
    <MyEquipmentContextProvider
      selectedEquipmentIndex={selectedEquipmentIndex}
      myEquipmentList={myEquipmentList ?? []}
    >
      <div className="d-none d-blg-block">
        <SelectedEquipmentHeading
          variant="title-sm"
          className="ps-4 pe-2"
          showTooltip={true}
          decorator
        >
          <>{t('CURRENTLY_SHOPPING_FOR_CAMEL')}</>
        </SelectedEquipmentHeading>
        <div className="row g-0 pt-3">
          <div className="col-6 ps-4 pe-2">
            <div className={styles['image-wrapper']}>{getEquipmentImage()}</div>
          </div>
          <div className="col-6 px-2">
            {getEquipmentHeading(assetName, 'title')}
            {getEquipmentHeading(equipmentName, modelVariant, !assetName)}
            {getEquipmentHeading(serialNumber, 'body-sm', false)}
            <Conditional test={!serialNumber}>
              <AddSerialButton
                variant="inline"
                classname={styles['no-padding']}
              />
            </Conditional>
            <ServiceInsightIcon
              equipmentDetails={selectedEquipment}
              serviceInsightsDetails={serviceInsights}
              isCspUser={userDetail?.isCspUser}
              isDisplayLink
              isLargeIcon
              classname="mt-2"
            />
          </div>
          <div className="d-block d-sm-block d-md-block pb-lg-0 ">
            <div className="ps-4 mt-4">
              <EquipmetActionButtonWrapper
                equipmentDetails={selectedEquipment}
                userDetail={userDetail}
                selectedEquipmentIndex={selectedEquipmentIndex}
                startTransition={startTransition}
                isPending={isPending}
                isAssetLandingFeatureFlag={isAssetLandingFeatureFlag}
              />
            </div>
          </div>
        </div>
      </div>
      <div
        className={`${styles['selected-equipment-wrapper']} d-blg-none p-3 p-md-4 `}
      >
        <div className={styles['image-wrapper']}>{getEquipmentImage()}</div>
        <div className="px-2 mx-1 px-md-0 mx-md-0 pe-md-3 d-block overflow-hidden">
          {getEquipmentHeading(assetName, prominentVariant)}
          {getEquipmentHeading(equipmentName, modelVariantSM)}
          {getEquipmentHeading(serialNumber, nonProminentVariant, false)}
          <div className={styles['service-insight-wrapper']}>
            <ServiceInsightIcon
              equipmentDetails={selectedEquipment}
              serviceInsightsDetails={serviceInsights}
              isCspUser={userDetail?.isCspUser}
            />
          </div>
          <Conditional test={!serialNumber}>
            <AddSerialButton
              variant="inline"
              classname={styles['no-padding']}
            />
          </Conditional>
        </div>
        <div
          className={`${styles['select-equipment-button']} w-100 pt-3 gap-2`}
        >
          <CatButton
            href={redirectToShopParts}
            data-testid="shopPartsButton"
            variant="contrast"
            size={isMD ? 'md' : 'sm'}
            fullWidth
            data-track-event="e_primaryCTA"
            data-track-attr-nltext="Shop Parts"
            data-track-attr-context="Homepage Menu"
            className={styles['quick-action-button']}
          >
            {t('SHOP_PARTS')}
          </CatButton>
          <Link
            href={redirectToPartsDiagram}
            className="d-flex text-decoration-none"
          >
            <CatButton
              data-testid="partsDiagramButton"
              size={isMD ? 'md' : 'sm'}
              fullWidth
              className={styles['quick-action-button']}
            >
              {t('CAT_PARTS_DIAGRAM')}
            </CatButton>
          </Link>
        </div>
      </div>
    </MyEquipmentContextProvider>
  );
};
